<template>
    <form-wrapper class="intake-activities" :noValidate="true" @submit="handleFormSubmission(data)">
        <intake-form-section class="intake-activities__ratings">
            <form-row v-for="activity in activityQuestions" :key="activity.path">
                <form-cell>
                    <tabs-field
                        :label="activity.label"
                        :options="difficultyOptions"
                        :formData="data"
                        :valuePath="activity.path"
                        size="small"
                        @change="change($event)"
                    ></tabs-field>
                </form-cell>
            </form-row>
        </intake-form-section>

        <intake-form-section :title="$t('forms.intake.daily_activities.sections.assistive_devices')">
            <p class="intake-activities__prompt">
                {{ $t('forms.intake.daily_activities.fields.assistive_devices.prompt') }}
            </p>

            <p v-if="!data.assistiveDevices.length" class="intake-activities__empty">
                {{ $t('forms.intake.daily_activities.empty') }}
            </p>

            <div v-for="(device, index) in data.assistiveDevices" :key="`device-${index}`" class="intake-activities__card">
                <div class="intake-activities__card-header">
                    <h4 class="intake-activities__card-title">
                        {{ $t('forms.intake.daily_activities.device_label', { number: index + 1 }) }}
                    </h4>
                    <common-button
                        :label="$t('common.microcopy.remove')"
                        type="secondary"
                        size="small"
                        :isSubmittable="false"
                        @click="removeDevice(index)"
                    ></common-button>
                </div>

                <form-row>
                    <form-cell>
                        <select-field
                            :label="$t('forms.intake.daily_activities.fields.device.label')"
                            :placeholder="$t('forms.intake.daily_activities.fields.device.placeholder')"
                            :isRequired="true"
                            :options="deviceOptions"
                            :formData="data"
                            :valuePath="`assistiveDevices.${index}.device`"
                            @change="change($event)"
                        ></select-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <checkbox-field
                            :formData="data"
                            :valuePath="`assistiveDevices.${index}.prescribedByProvider`"
                            @change="change($event)"
                        >
                            <template #label>
                                <span>
                                    {{ $t('forms.intake.daily_activities.fields.prescribed_by_provider.label') }}
                                </span>
                            </template>
                        </checkbox-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.intake.daily_activities.fields.how_often.label')"
                            :placeholder="$t('forms.intake.daily_activities.fields.how_often.placeholder')"
                            :formData="data"
                            :valuePath="`assistiveDevices.${index}.howOften`"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>
            </div>

            <button class="intake-activities__add" type="button" @click="addDevice">
                + {{ $t('forms.intake.daily_activities.actions.add_device') }}
            </button>
        </intake-form-section>

        <template #footer>
            <common-buttons>
                <common-button
                    :label="$t('common.microcopy.back')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="goBack"
                ></common-button>
                <common-button
                    :label="$t('common.microcopy.next')"
                    type="primary"
                    size="large"
                    :isProcessing="isSending"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import IntakeFormSection from 'components/forms/intake/IntakeFormSection'

    const ACTIVITY_FIELDS = [
        'walking',
        'standing',
        'sitting',
        'bending',
        'lifting',
        'climbing_stairs',
        'driving',
        'sleeping',
        'dressing',
        'bathing',
        'household_chores',
        'shopping',
        'exercise',
        'work',
        'childcare_caregiving',
        'social_recreational',
    ]

    const DIFFICULTY_OPTIONS = ['no_difficulty', 'some_difficulty', 'significant_difficulty', 'unable', 'not_applicable']

    const DEVICE_OPTIONS = ['cane', 'walker', 'crutches', 'brace', 'cervical_collar', 'wheelchair', 'other']

    export default {
        mixins: [FormMixin],

        components: {
            IntakeFormSection,
        },

        props: {
            initialData: { type: Object, default: undefined },
        },

        data() {
            const activityDefaults = ACTIVITY_FIELDS.reduce((acc, path) => {
                acc[path] = 'not_applicable'
                return acc
            }, {})

            return {
                data: {
                    ...activityDefaults,
                    assistiveDevices: [],
                    ...(this.initialData || {}),
                },
            }
        },

        computed: {
            activityQuestions() {
                return ACTIVITY_FIELDS.map((path) => ({
                    path,
                    label: this.$t(`forms.intake.daily_activities.fields.activities.${path}`),
                }))
            },

            difficultyOptions() {
                return DIFFICULTY_OPTIONS.map((value) => ({
                    label: this.$t(`forms.intake.daily_activities.fields.difficulty.options.${value}`),
                    value,
                }))
            },

            deviceOptions() {
                return DEVICE_OPTIONS.map((value) => ({
                    label: this.$t(`forms.intake.daily_activities.fields.device.options.${value}`),
                    value,
                }))
            },
        },

        methods: {
            createDevice() {
                return {
                    device: undefined,
                    prescribedByProvider: false,
                    howOften: undefined,
                }
            },

            addDevice() {
                this.change({
                    path: 'assistiveDevices',
                    value: [...this.data.assistiveDevices, this.createDevice()],
                })
            },

            removeDevice(index) {
                const next = [...this.data.assistiveDevices]
                next.splice(index, 1)
                this.change({ path: 'assistiveDevices', value: next })
            },

            goBack() {
                this.emitEvent({ name: 'back', payload: this.data })
            },

            handleFormSubmission(data) {
                this.isSending = true
                this.emitSent(data)
                this.isSending = false
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';

    .intake-activities__ratings {
        overflow: hidden;

        ::v-deep .form-input-tabs {
            display: flex;
            flex-wrap: wrap;
            gap: rem(8px);
            width: 100%;
            max-width: 100%;

            > div {
                flex: 1 1 calc(33.333% - #{rem(6px)});
                min-width: rem(88px);

                &:not(:first-child) label {
                    margin-left: 0;
                }

                label {
                    border-radius: rem($border-radius--base);
                }

                input[data-checked] + label {
                    border-color: $color--accent-1;
                }

                input[data-checked] + label::before,
                input[data-checked] + label::after {
                    display: none;
                }
            }

            &--size-small label {
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                min-height: rem(40px);
                padding: rem(8px) rem(6px);
                font-size: rem(12px);
                line-height: 1.25;
                white-space: normal;
                overflow-wrap: break-word;
                word-break: normal;
                box-sizing: border-box;
            }
        }
    }

    .intake-activities__prompt {
        margin: 0 0 rem(12px);
        font-size: rem($font-size--form);
        color: $color--txt-base;
    }

    .intake-activities__empty {
        margin: 0 0 rem(12px);
        font-size: rem($font-size--form);
        color: $color--txt-minor;
    }

    .intake-activities__card {
        margin-bottom: rem(16px);
        padding: rem(16px);
        border: rem(1px) solid $color--border-base;
        border-radius: rem($border-radius--base);
        background: $color--bg-dropdown;
    }

    .intake-activities__card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: rem(12px);
        margin-bottom: rem(12px);
        flex-wrap: wrap;
    }

    .intake-activities__card-title {
        margin: 0;
        font-size: rem($font-size--form);
        font-weight: 600;
        color: $color--txt-header;
    }

    .intake-activities__add {
        display: block;
        width: 100%;
        margin-top: rem(4px);
        padding: rem(14px) rem(12px);
        border: rem(1px) dashed $color--accent-1;
        border-radius: rem($border-radius--base);
        background: transparent;
        color: $color--accent-1;
        font-size: rem($font-size--form);
        font-weight: 600;
        text-align: center;
        cursor: pointer;

        &:hover {
            background: rgba($color--accent-1, 0.06);
        }
    }
</style>
